import { Component, OnInit } from '@angular/core';
import { Router, ActivatedRoute } from '@angular/router';
import { ToastComponent } from 'src/app/shared/toast/toast.component';
import { CrudServiceService } from 'src/app/services/crudService/crud-service.service';
import { environment } from 'src/environments/environment';
import { FormBuilder, FormGroup, FormArray, Validators } from '@angular/forms';

@Component({
  selector: 'app-add-prescription',
  templateUrl: './add-prescription.component.html',
  styleUrls: ['./add-prescription.component.scss']
})
export class AddPrescriptionComponent implements OnInit {
  prescriptionForm: FormGroup;
  departments: any[] = [];
  id: string | null = null;
  isLoading = false;
  isFormLoading = false;

  constructor(
    private crudService: CrudServiceService,
    private router: Router,
    private snackbar: ToastComponent,
    private activatedRoute: ActivatedRoute,
    private fb: FormBuilder
  ) {
    this.id = this.activatedRoute.snapshot.paramMap.get("id");
    this.initializeForm();
  }

  ngOnInit(): void {
    this.loadDepartments().then(() => {
      if (this.id) {
        this.loadPrescription();
      }
    });
  }

  initializeForm() {
    this.prescriptionForm = this.fb.group({
      treatment: ['', Validators.required],
      department_id: [null, Validators.required],
      treatmentNotes: this.fb.array([]),
      symptoms: this.fb.array([]),
      tests: this.fb.array([]),
      prescribed_drugs: this.fb.array([])
    });
  }

  get treatmentNotes(): FormArray {
    return this.prescriptionForm.get('treatmentNotes') as FormArray;
  }

  get symptoms(): FormArray {
    return this.prescriptionForm.get('symptoms') as FormArray;
  }

  get tests(): FormArray {
    return this.prescriptionForm.get('tests') as FormArray;
  }

  get prescribedDrugs(): FormArray {
    return this.prescriptionForm.get('prescribed_drugs') as FormArray;
  }

  async loadDepartments(): Promise<void> {
    this.isLoading = true;
    try {
      const res: any = await this.crudService.getAll(environment.api.department, {});
      if (res && res.data && Array.isArray(res.data)) {
        this.departments = res.data;
      } else {
        console.warn("Unexpected response format:", res);
        this.departments = [];
      }
    } catch (err) {
      console.error("Error loading departments:", err);
      this.snackbar.toastError("Failed to load departments");
    } finally {
      this.isLoading = false;
    }
  }

  loadPrescription() {
    this.isFormLoading = true;
    this.crudService.getDedtail(this.id, `${environment.api.template}/prescription`).then((res: any) => {
      if (res && res.data) {
        const prescriptionData = res.data;
        
        // Clear existing arrays
        this.clearFormArrays();

        // Patch the main form values
        this.prescriptionForm.patchValue({
          treatment: prescriptionData.treatment,
          department_id: prescriptionData.department_id
        });

        // Load arrays
        this.loadFormArrays(prescriptionData);
      }
    }).catch(err => {
      console.error("Error loading prescription:", err);
      this.snackbar.toastError("Failed to load prescription template");
    }).finally(() => {
      this.isFormLoading = false;
    });
  }

  private clearFormArrays() {
    const clearArray = (array: FormArray) => {
      while (array.length) {
        array.removeAt(0);
      }
    };

    clearArray(this.treatmentNotes);
    clearArray(this.symptoms);
    clearArray(this.tests);
    clearArray(this.prescribedDrugs);
  }

  private loadFormArrays(data: any) {
    if (data.treatmentNotes?.length) {
      data.treatmentNotes.forEach((note: string) => this.addTreatmentNote(note));
    }

    if (data.symptoms?.length) {
      data.symptoms.forEach((symptom: string) => this.addSymptom(symptom));
    }

    if (data.tests?.length) {
      data.tests.forEach((test: string) => this.addTest(test));
    }

    if (data.prescribed_drugs?.length) {
      data.prescribed_drugs.forEach((drug: any) => this.addDrug(drug));
    }
  }

  addTreatmentNote(note: string = '') {
    this.treatmentNotes.push(this.fb.control(note));
  }

  removeTreatmentNote(index: number) {
    this.treatmentNotes.removeAt(index);
  }

  addSymptom(symptom: string = '') {
    this.symptoms.push(this.fb.control(symptom));
  }

  removeSymptom(index: number) {
    this.symptoms.removeAt(index);
  }

  addTest(test: string = '') {
    this.tests.push(this.fb.control(test));
  }

  removeTest(index: number) {
    this.tests.removeAt(index);
  }

  addDrug(drug: any = null) {
    this.prescribedDrugs.push(this.fb.group({
      name: [drug?.name || '', Validators.required],
      dosage: [drug?.dosage || ''],
      quantity: [drug?.quantity || ''],
      duration: [drug?.duration || ''],
      repeat: [drug?.repeat || ''],
      timeoftheday: [drug?.timeoftheday || ''],
      tobeTaken: [drug?.tobeTaken || ''],
      type: [drug?.type || ''],
      remark: [drug?.remark || '']
    }));
  }

  removeDrug(index: number) {
    this.prescribedDrugs.removeAt(index);
  }

  savePrescription() {
    if (this.prescriptionForm.invalid) {
      this.prescriptionForm.markAllAsTouched();
      this.snackbar.toastError("Please fill all required fields");
      return;
    }

    if (this.id) {
      this.updatePrescription();
    } else {
      this.createPrescription();
    }
  }

  createPrescription() {
    this.crudService.add(this.prescriptionForm.value, `${environment.api.template}/prescription`)
      .then((res: any) => {
        this.snackbar.toastSuccess("Prescription template created successfully");
        this.router.navigate(['/admin/prescription/list']);
      })
      .catch(err => {
        console.error("Error creating prescription:", err);
        this.snackbar.toastError("Failed to create prescription template");
      });
  }

  updatePrescription() {
    this.crudService.edit(this.prescriptionForm.value, `${environment.api.template}/prescription/${this.id}`)
      .then((res: any) => {
        this.snackbar.toastSuccess("Prescription template updated successfully");
        this.router.navigate(['/admin/prescription/list']);
      })
      .catch(err => {
        console.error("Error updating prescription:", err);
        this.snackbar.toastError("Failed to update prescription template");
      });
  }
}